<template>
  <div class="scan-stock-query-container">
    <header class="header">
      <button @click="handleBack" class="back-btn" aria-label="返回">
        <img src="@/assets/返回.svg" alt="返回" class="icon-back" />
      </button>
      <h1 class="title">扫码页面</h1>
    </header>

    <main class="content">
      <!-- 状态提示横幅 -->
      <div v-if="statusMsg" class="status-banner" :class="statusType">
        {{ statusMsg }}
      </div>

      <!-- 扫码输入区 -->
      <div class="form-section source-section">
        <h3 class="section-title">扫码输入</h3>
        <div class="form-group">
          <input 
            id="sourceDataInput"
            type="text" 
            v-model="sourceData" 
            placeholder="扫描数据后自动解析（格式：物料编码*数量*供应商编码）"
            @input="handleInputChange"
            autocomplete="off"
          />
        </div>
        
        <!-- 操作按钮组 -->
        <div class="source-actions">
          <button @click="handleSourceParse" class="btn parse-btn" :disabled="isParsing">
            {{ isParsing ? '解析中...' : '解析盘点' }}
          </button>
          <button @click="openManualInput" class="btn manual-input-btn">
            手动输入
          </button>
          <button @click="clearSourceData" class="btn clear-btn">
            清空
          </button>
        </div>
        <p class="pda-hint">提示：扫描后自动解析</p>
      </div>
      
      <!-- 盘点详情列表 -->
      <div class="form-section result-section">
        <h3 class="section-title">盘点详情</h3>

        <!-- 加载状态 -->
        <div v-if="loading" class="loading-state">
          <div class="loading-spinner"></div>
          <p class="loading-text">加载盘点详情中...</p>
        </div>

        <!-- 错误提示 -->
        <div v-if="errorMsg" class="error-message">{{ errorMsg }}</div>

        <!-- 空状态 -->
        <div v-if="!loading && !errorMsg && inventoryData.length === 0" class="empty-state">
          <div class="empty-icon">📦</div>
          <p class="empty-text">暂无盘点数据</p>
        </div>

        <!-- 盘点详情表格 -->
        <div v-else-if="!loading && !errorMsg && inventoryData.length > 0" class="detail-table-container">
          <div class="table-header">
            <div class="table-col batch-col">批次号</div>
            <div class="table-col">库存数量</div>
            <div class="table-col">盘点数量</div>
            <div class="table-col">差异数量</div>
            <div class="table-col">状态</div>
          </div>
          <div class="table-body">
            <div 
              v-for="item in inventoryData" 
              :key="item.id" 
              class="table-row"
              :class="{ 'highlight-row': item.highlight }"
            >
              <div class="table-col batch-col">{{ item.batchNo }}</div>
              <div class="table-col">{{ item.stockNum }}</div>
              <div class="table-col">{{ item.actualNum }}</div>
              <div class="table-col" :class="{ 'positive-diff': parseFloat(item.differenceNum) > 0, 'negative-diff': parseFloat(item.differenceNum) < 0 }">{{ item.differenceNum }}</div>
              <div class="table-col">
                <span class="status-tag" :class="getStatusClass(item.isInvente)">
                  {{ item.isInvente === 1 ? '已盘点' : '未盘点' }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- 手动输入弹窗 -->
    <div v-if="showManualInput" class="modal-overlay">
      <div class="modal-container">
        <div class="modal-header">
          <h3 class="modal-title">手动输入盘点信息</h3>
          <button @click="closeManualInput" class="modal-close" aria-label="关闭">×</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label for="manualMaterialNo">物料编码</label>
            <input 
              id="manualMaterialNo" 
              v-model="manualInput.materialNo" 
              type="text" 
              placeholder="请输入物料编码"
              @input="trimInput('materialNo')"
              @blur="handleMaterialNoBlur"
            >
          </div>
          <div class="form-group">
            <label for="manualSupplierCode">供应商编码</label>
            <input 
              id="manualSupplierCode" 
              v-model="manualInput.supplierCode" 
              type="text" 
              placeholder="请输入供应商编码"
              @input="trimInput('supplierCode')"
              @blur="handleSupplierCodeBlur"
            >
          </div>
          <div class="form-group">
            <label for="manualBatchNo">批次号</label>
            <input 
              id="manualBatchNo" 
              v-model="manualInput.batchNo" 
              type="text" 
              placeholder="请输入批次号"
              @input="trimInput('batchNo')"
            >
          </div>
          <div class="form-group">
            <label for="manualQuantity">数量</label>
            <input 
              id="manualQuantity" 
              v-model="manualInput.quantity" 
              type="number" 
              placeholder="请输入数量"
              min="0"
              step="0.01"
            >
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeManualInput" class="btn modal-cancel">取消</button>
          <button @click="confirmManualInput" class="btn modal-confirm" :disabled="!isManualInputValid">
            确认盘点
          </button>
        </div>
      </div>
    </div>

    <!-- 错误提示弹窗 -->
    <div v-if="showErrorModal" class="modal-overlay">
      <div class="modal-container">
        <div class="modal-header">
          <h3 class="modal-title">{{ errorModalTitle }}</h3>
          <button @click="showErrorModal = false" class="modal-close" aria-label="关闭">×</button>
        </div>
        <div class="modal-body">
          <p class="error-message">{{ errorModalDetail }}</p>
          <p class="error-tip" v-if="errorModalAdvice">{{ errorModalAdvice }}</p>
        </div>
        <div class="modal-footer">
          <button @click="showErrorModal = false" class="btn modal-confirm">确定</button>
        </div>
      </div>
    </div>

    <!-- 已盘点提示弹窗 -->
    <div v-if="showAlreadyCheckedModal" class="modal-overlay">
      <div class="modal-container">
        <div class="modal-header">
          <h3 class="modal-title">已盘点提示</h3>
          <button @click="showAlreadyCheckedModal = false" class="modal-close" aria-label="关闭">×</button>
        </div>
        <div class="modal-body">
          <p class="info-message">该物料批次已盘点，无需重复操作。</p>
        </div>
        <div class="modal-footer">
          <button @click="showAlreadyCheckedModal = false" class="btn modal-confirm">确定</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, nextTick, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { getId, updateStockCheckDetail } from '@/api/stockCheck'
import * as stockApi from '@/api/stockQuery'

// 辅助函数：保留两位小数
const formatToTwoDecimals = (num) => {
  return parseFloat(num).toFixed(2)
}

export default {
  name: 'CompleteScanPage',
  setup() {
    const router = useRouter()
    const route = useRoute()
    const inventoryId = route.params.id
    
    // 基础状态管理
    const sourceData = ref('') // 扫码输入内容
    const isParsing = ref(false) // 解析中状态
    const statusMsg = ref('') // 状态提示文本
    const statusType = ref('success') // 状态类型：success/error/warn
    const inventoryData = ref([]) // 盘点数据列表
    const loading = ref(false) // 加载状态
    const errorMsg = ref('') // 错误信息

    // 弹窗控制
    const showManualInput = ref(false) // 手动输入弹窗
    const showErrorModal = ref(false) // 错误提示弹窗
    const showAlreadyCheckedModal = ref(false) // 已盘点提示弹窗

    // 错误弹窗内容
    const errorModalTitle = ref('提示')
    const errorModalDetail = ref('')
    const errorModalAdvice = ref('')

    // 手动输入表单
    const manualInput = ref({
      materialNo: '', // 物料编码
      supplierCode: '', // 供应商编码
      supplierId: '', // 供应商ID
      supplierName: '', // 供应商名称
      batchNo: '', // 批次号
      quantity: 1.00 // 默认数量改为带两位小数
    })

    // 音频上下文（提示音）
    let audioContext = null
    const isAudioContextActive = ref(false)

    // 解析计时器
    let parseTimer = null

    // 计算属性：手动输入表单验证
    const isManualInputValid = computed(() => {
      return manualInput.value.materialNo.trim() && 
             manualInput.value.supplierCode.trim() && 
             manualInput.value.batchNo.trim() && 
             parseFloat(manualInput.value.quantity) > 0
    })

    // 初始化音频上下文
    const initAudioContext = () => {
      if (audioContext || isAudioContextActive.value) return

      try {
        const AudioContextConstructor = window.AudioContext || window.webkitAudioContext
        audioContext = new AudioContextConstructor()
        
        if (audioContext.state === 'suspended') {
          audioContext.resume().then(() => {
            isAudioContextActive.value = true
          }).catch(err => console.warn('激活音频上下文失败：', err))
        } else {
          isAudioContextActive.value = true
        }

        // 仅初始化一次
        document.removeEventListener('click', initAudioContext)
        document.removeEventListener('touchstart', initAudioContext)
      } catch (err) {
        console.warn('创建音频上下文失败：', err)
      }
    }

    // 播放成功提示音
    const playSuccessBeep = () => {
      try {
        if (!audioContext) {
          const AudioContextConstructor = window.AudioContext || window.webkitAudioContext
          audioContext = new AudioContextConstructor()
        }

        if (audioContext.state === 'suspended') {
          audioContext.resume().catch(err => {
            console.warn('恢复音频上下文失败：', err)
            return
          })
        }

        const oscillator = audioContext.createOscillator()
        const gainNode = audioContext.createGain()
        oscillator.connect(gainNode)
        gainNode.connect(audioContext.destination)

        oscillator.type = 'sine'
        oscillator.frequency.setValueAtTime(1000, audioContext.currentTime)
        gainNode.gain.setValueAtTime(1, audioContext.currentTime)

        oscillator.start()
        oscillator.stop(audioContext.currentTime + 0.3)
        oscillator.onended = () => {
          oscillator.disconnect()
          gainNode.disconnect()
        }
      } catch (error) {
        console.warn('播放提示音失败：', error)
      }
    }

    // 播放错误提示音
    const playErrorBeep = () => {
      try {
        if (!audioContext) {
          const AudioContextConstructor = window.AudioContext || window.webkitAudioContext
          audioContext = new AudioContextConstructor()
        }

        if (audioContext.state === 'suspended') {
          audioContext.resume().catch(err => {
            console.warn('恢复音频上下文失败：', err)
            return
          })
        }

        const oscillator = audioContext.createOscillator()
        const gainNode = audioContext.createGain()
        oscillator.connect(gainNode)
        gainNode.connect(audioContext.destination)

        oscillator.type = 'sine'
        oscillator.frequency.setValueAtTime(2200, audioContext.currentTime)
        gainNode.gain.setValueAtTime(2, audioContext.currentTime)

        // 3次短音提示
        for (let i = 0; i < 3; i++) {
          setTimeout(() => {
            gainNode.gain.setValueAtTime(2, audioContext.currentTime)
            setTimeout(() => gainNode.gain.setValueAtTime(0, audioContext.currentTime), 100)
          }, i * 200)
        }

        oscillator.start()
        oscillator.onended = () => {
          oscillator.disconnect()
          gainNode.disconnect()
        }
      } catch (error) {
        console.warn('播放提示音失败：', error)
      }
    }

    // 设置状态提示
    const setStatus = (message, type = 'success') => {
      statusMsg.value = message
      statusType.value = type
      setTimeout(() => {
        if (statusMsg.value === message) statusMsg.value = ''
      }, 3000)
    }

    // 处理扫码输入变化（自动触发解析）
    const handleInputChange = () => {
      if (parseTimer) clearTimeout(parseTimer)
      const currentData = (sourceData.value || '').trim()
      
      // 检测到换行符时立即解析单条数据
      if (currentData.includes('\n') || currentData.includes('\r')) {
        const lineBreakMatch = currentData.match(/[\n\r]/)
        if (lineBreakMatch) {
          const firstData = currentData.substring(0, lineBreakMatch.index).trim()
          if (firstData) {
            if (parseTimer) clearTimeout(parseTimer)
            sourceData.value = currentData.substring(lineBreakMatch.index + 1).trim()
            handleSourceParse(firstData)
          }
        }
      }
      // 长度触发解析（使用与库存查询相同的15字符阈值和300ms延迟）
      else if (currentData.length > 15) {
        parseTimer = setTimeout(() => {
          handleSourceParse()
        }, 300)
      }
    }

    // 检查是否已盘点
    const checkIfAlreadyChecked = (materialNo, batchNo) => {
      // 查找是否有相同物料和批次且已盘点的记录
      return inventoryData.value.some(item => 
        item.materialNo === materialNo && 
        item.batchNo === batchNo && 
        item.isInvente === 1
      )
    }

    // 解析扫码数据并处理盘点
    const handleSourceParse = async (singleData = null) => {
      if (isParsing.value) return
      isParsing.value = true

      try {
        await nextTick()
        const cleanData = singleData ? singleData.trim() : sourceData.value.trim()
        if (!cleanData) {
          setStatus('请输入有效的扫描数据', 'warn')
          return
        }

        // 修复：处理混合分隔符的情况
        // 首先将所有非星号分隔符替换为星号
        const normalizedData = cleanData.replace(/×/g, '*') // 替换乘号为星号
        
        // 然后再按星号分割
        const fields = normalizedData.split('*').map(item => item.trim())
        
        // 验证基本格式
        if (fields.length < 3 || !fields[0] || !fields[1] || !fields[2]) {
          setStatus('数据格式错误，应为：物料编码*数量*供应商编码*...*批次号', 'error')
          playErrorBeep()
          return
        }

        // 提取核心字段 - 将数量解析为浮点数并保留两位小数
        const materialNo = fields[0]
        const quantity = parseFloat(fields[1]) || 0 // 改为parseFloat支持小数
        const formattedQuantity = formatToTwoDecimals(quantity) // 格式化保留两位小数
        const supplierCode = fields[2]
        
        // 修复：正确提取批次号
        // 批次号在数据中是最后一个非空字段，即 B1100992534000757
        // 我们从后向前查找非空字段
        let batchNo = '';
        for (let i = fields.length - 1; i >= 0; i--) {
          if (fields[i]) {
            batchNo = fields[i];
            break;
          }
        }

        console.log('提取的批次号:', batchNo); // 添加日志，用于调试

        if (!batchNo) {
          setStatus('未找到批次号，请检查条码格式', 'error')
          playErrorBeep()
          return
        }

        // 检查数量有效性（必须大于0）
        if (parseFloat(formattedQuantity) <= 0) {
          setStatus('数量必须大于0', 'error')
          playErrorBeep()
          return
        }

        // 检查是否已盘点
        if (checkIfAlreadyChecked(materialNo, batchNo)) {
          showAlreadyCheckedModal.value = true
          // 清空输入框
          if (!singleData) sourceData.value = ''
          document.getElementById('sourceDataInput')?.focus()
          return
        }

        // 验证供应商是否存在
        const supplierRes = await stockApi.getSupplierByCode(supplierCode)
        const supplierList = supplierRes.data?.records || []
        if (supplierList.length === 0) {
          showErrorModal.value = true
          errorModalDetail.value = `供应商编码「${supplierCode}」不存在`
          errorModalAdvice.value = '请检查供应商编码是否正确'
          playErrorBeep()
          return
        }

        // 验证物料是否存在
        const selectedSupplier = supplierList[0]; // 获取选定的供应商
        const materialRes = await stockApi.getMaterialByNo(materialNo, selectedSupplier?.id); // 传入供应商ID
        if (!materialRes.success || !materialRes.data || !materialRes.data.records || materialRes.data.records.length === 0) {
          showErrorModal.value = true
          errorModalDetail.value = `物料编码「${materialNo}」不存在`
          errorModalAdvice.value = '请检查物料编码是否正确'
          playErrorBeep()
          return
        }

        // 直接执行盘点操作
        setStatus(`正在盘点: 物料=${materialNo}, 批次=${batchNo}, 数量=${formattedQuantity}`, 'success')
        
        // 保存到后端
        await saveStockCheckDetail(materialNo, batchNo, formattedQuantity, materialRes)
        
        // 清空输入框
        if (!singleData) sourceData.value = ''
        document.getElementById('sourceDataInput')?.focus()
      } catch (err) {
        console.error('解析盘点失败：', err)
        setStatus(`解析失败：${err.message || '系统异常'}`, 'error')
        playErrorBeep()
      } finally {
        isParsing.value = false
      }
    }

    // 获取状态类名
    const getStatusClass = (status) => {
      return `status-${status}`;
    };

    // 手动输入相关方法
    const trimInput = (field) => {
      if (typeof manualInput.value[field] === 'string') {
        manualInput.value[field] = manualInput.value[field].trim()
      }
    }

    const handleSupplierCodeBlur = async () => {
      const code = manualInput.value.supplierCode.trim()
      if (!code) {
        manualInput.value.supplierId = ''
        manualInput.value.supplierName = ''
        return
      }

      try {
        const res = await stockApi.getSupplierByCode(code)
        const supplierList = res.data?.records || []
        if (supplierList.length > 0) {
          manualInput.value.supplierId = supplierList[0].id
          manualInput.value.supplierName = supplierList[0].supplierName
        } else {
          manualInput.value.supplierId = ''
          manualInput.value.supplierName = ''
          showErrorModal.value = true
          errorModalDetail.value = `供应商编码「${code}」不存在`
          errorModalAdvice.value = '请检查供应商编码是否正确'
        }
      } catch (err) {
        console.error('查询供应商失败：', err)
        setStatus('查询供应商信息失败，请稍后重试', 'error')
      }
    }

    const handleMaterialNoBlur = async () => {
      const materialNo = manualInput.value.materialNo.trim()
      if (!materialNo) return

      try {
        const res = await stockApi.getMaterialByNo(materialNo)
        if (!res.success || !res.data) {
          showErrorModal.value = true
          errorModalDetail.value = `物料编码「${materialNo}」不存在`
          errorModalAdvice.value = '请检查物料编码是否正确'
        }
      } catch (err) {
        console.error('查询物料失败：', err)
        setStatus('查询物料信息失败，请稍后重试', 'error')
      }
    }

    const openManualInput = () => {
      manualInput.value = {
        materialNo: '',
        supplierCode: '',
        supplierId: '',
        supplierName: '',
        batchNo: '',
        quantity: 1.00 // 手动输入默认值改为带两位小数
      }
      showManualInput.value = true
      setTimeout(() => {
        document.getElementById('manualMaterialNo')?.focus()
      }, 300)
    }

    const closeManualInput = () => {
      showManualInput.value = false
    }

    const confirmManualInput = async () => {
      try {
        const { materialNo, supplierCode, batchNo, quantity } = manualInput.value
        
        // 格式化数量为两位小数
        const formattedQuantity = formatToTwoDecimals(quantity)
        
        // 检查数量有效性
        if (parseFloat(formattedQuantity) <= 0) {
          setStatus('数量必须大于0', 'error')
          playErrorBeep()
          return
        }

        // 检查是否已盘点
        if (checkIfAlreadyChecked(materialNo, batchNo)) {
          showAlreadyCheckedModal.value = true
          showManualInput.value = false
          return
        }

        // 验证供应商
        const supplierRes = await stockApi.getSupplierByCode(supplierCode)
        const supplierList = supplierRes.data?.records || []
        if (supplierList.length === 0) {
          showErrorModal.value = true
          errorModalDetail.value = `供应商编码「${supplierCode}」不存在`
          return
        }

        // 验证物料
        const selectedSupplier = supplierList[0]; // 获取选定的供应商
        const materialRes = await stockApi.getMaterialByNo(materialNo, selectedSupplier?.id); // 传入供应商ID
        if (!materialRes.success || !materialRes.data || !materialRes.data.records || materialRes.data.records.length === 0) {
          showErrorModal.value = true
          errorModalDetail.value = `物料编码「${materialNo}」不存在`
          return
        }

        // 直接执行盘点操作
        setStatus(`正在盘点: 物料=${materialNo}, 批次=${batchNo}, 数量=${formattedQuantity}`, 'success')
        
        // 保存到后端
        await saveStockCheckDetail(materialNo, batchNo, formattedQuantity, materialRes)

        showManualInput.value = false
      } catch (err) {
        console.error('手动输入盘点失败：', err)
        setStatus(`操作失败：${err.message || '系统异常'}`, 'error')
        playErrorBeep()
      }
    };

    // 其他操作
    const clearSourceData = () => {
      sourceData.value = ''
      if (parseTimer) clearTimeout(parseTimer)
      document.getElementById('sourceDataInput')?.focus()
    }

    const handleBack = () => {
      router.back()
    }

    // 更新获取盘点详情数据的方法
    const fetchInventoryDetails = async () => {
      try {
        loading.value = true;
        errorMsg.value = '';
        
        const response = await getId(inventoryId);
        
        if (response.success && response.data) {
          // 初始化数据，确保所有必要字段存在且数量保留两位小数
          inventoryData.value = response.data.map(item => ({
            ...item,
            actualNum: formatToTwoDecimals(item.actualNum || 0), // 格式化数量为两位小数
            differenceNum: formatToTwoDecimals(item.differenceNum || 0), // 格式化差异为两位小数
            highlight: false
          }));
        } else {
          errorMsg.value = response.msg || '获取盘点详情失败';
        }
      } catch (err) {
        errorMsg.value = '获取盘点详情失败，请稍后重试';
        console.error(err);
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => {
      // 初始化音频
      document.addEventListener('click', initAudioContext, { once: true });
      document.addEventListener('touchstart', initAudioContext, { once: true });
      
      // 获取盘点详情数据
      fetchInventoryDetails();
      
      // 输入框自动聚焦
      document.getElementById('sourceDataInput')?.focus()
    });

    // 保存盘点结果的方法，使其能够处理新的盘点项
    const saveStockCheckDetail = async (materialNo, batchNo, quantity, materialRes) => {
      try {
        // 修复：正确获取物料ID，从data.records[0].id中获取
        const materialId = materialRes.data?.records?.[0]?.id || '';
        
        if (!materialId) {
          setStatus('无法获取物料ID，请重试', 'error');
          playErrorBeep();
          return;
        }
        
        // 准备请求数据 - 确保数量为数值类型
        const requestData = {
          inventeId: inventoryId,
          materialId: materialId, // 使用正确获取的物料ID
          batchNo: batchNo,
          actualNum: parseFloat(quantity) // 转为数值类型提交
        };

        console.log('保存盘点数据:', requestData); // 添加日志便于调试
        
        // 调用更新接口
        const response = await updateStockCheckDetail(requestData);
        
        if (response.success) {
          setStatus('盘点成功', 'success');
          playSuccessBeep();
          
          // 刷新页面数据，确保显示最新状态
          await fetchInventoryDetails();
        } else {
          setStatus(`盘点失败：${response.msg || '未知错误'}`, 'error');
          playErrorBeep();
        }
      } catch (error) {
        console.error('保存盘点数据失败:', error);
        setStatus(`盘点失败：${error.message}`, 'error');
        playErrorBeep();
      }
    };

    onUnmounted(() => {
      if (parseTimer) clearTimeout(parseTimer)
    })

    return {
      // 状态变量
      sourceData,
      isParsing,
      statusMsg,
      statusType,
      inventoryData,
      loading,
      errorMsg,
      showManualInput,
      showErrorModal,
      showAlreadyCheckedModal,
      errorModalTitle,
      errorModalDetail,
      errorModalAdvice,
      manualInput,
      
      // 计算属性
      isManualInputValid,
      
      // 方法
      handleInputChange,
      handleSourceParse,
      getStatusClass,
      trimInput,
      handleSupplierCodeBlur,
      handleMaterialNoBlur,
      openManualInput,
      closeManualInput,
      confirmManualInput,
      clearSourceData,
      handleBack,
      saveStockCheckDetail
    }
  }
}
</script>
    


<style>
/* 基础样式保持不变，增加弹窗相关样式 */
.scan-stock-query-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
  background-color: var(--background-color);
  color: var(--text-primary);
  font-size: 14px;
}

/* 头部样式 */
.header {
  display: flex;
  align-items: center;
  padding: 3px 16px;
  background-color: var(--header-background);
  color: var(--header-text);
  box-shadow: 0 2px 8px var(--shadow-light);
  position: sticky;
  top: 0;
  z-index: 100;
}

.back-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
}

.back-btn:hover {
  background: var(--card-background);
}

.icon-back {
  width: 24px;
  height: 24px;
}

.title {
  flex: 1;
  font-size: 18px;
  text-align: center;
  margin: 0;
  font-weight: 600;
}

/* 内容区域样式 */
.content {
  flex: 1;
  padding: 12px;
  overflow-y: auto;
  box-sizing: border-box;
  padding-bottom: 20px;
}

/* 表单区域样式 */
.form-section {
  background: var(--card-background);
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px var(--shadow-light);
}

.section-title {
  font-size: 16px;
  color: var(--text-primary);
  margin-bottom: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
}

.section-title::before {
  content: '';
  width: 4px;
  height: 18px;
  background-color: var(--primary-color);
  margin-right: 8px;
  border-radius: 2px;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 500;
}

.form-group input {
  width: 100%;
  padding: 12px 14px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  font-size: 14px;
  box-sizing: border-box;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
  min-height: 44px;
  background-color: var(--card-background);
  color: var(--text-primary);
}

.form-group input:focus {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px var(--shadow-light);
}

/* 按钮样式 */
.btn {
  min-height: 44px;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  font-weight: 500;
  transition: background-color 0.2s;
}

.source-actions {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}

.parse-btn {
  flex: 1;
  background-color: var(--primary-color);
  color: var(--primary-text);
}

.parse-btn:hover:not(:disabled) {
  background-color: var(--primary-hover);
}

.parse-btn:disabled {
  background-color: var(--primary-color);
  opacity: 0.5;
  cursor: not-allowed;
}

.manual-input-btn {
  flex: 1;
  background-color: #f59e0b;
  color: #fff;
}

.manual-input-btn:hover {
  background-color: #d97706;
}

.clear-btn {
  flex: 1;
  background-color: #ef4444;
  color: #fff;
}

.clear-btn:hover {
  background-color: #dc2626;
}

/* 状态提示样式 */
.status-banner {
  padding: 12px;
  margin-bottom: 16px;
  border-radius: 8px;
  font-size: 14px;
  text-align: center;
  font-weight: 500;
  transition: opacity 0.3s;
}

.status-banner.success {
  background-color: rgba(16, 185, 129, 0.12);
  color: #10b981;
  border: 1px solid rgba(16, 185, 129, 0.3);
}

.status-banner.error {
  background-color: rgba(239, 68, 68, 0.12);
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.3);
}

.status-banner.warn {
  background-color: rgba(245, 158, 11, 0.12);
  color: #f59e0b;
  border: 1px solid rgba(245, 158, 11, 0.3);
}

/* 表格样式 */
.detail-table-container {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

/* 表格样式 */
.detail-table-container {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

/* 添加差异数量样式 */
.positive-diff {
  color: #10b981; /* 绿色，与已盘点状态颜色一致 */
  font-weight: 500;
}

.negative-diff {
  color: #ef4444; /* 红色，与错误提示颜色一致 */
  font-weight: 600;
}

.table-header {
  display: flex;
  background-color: rgba(35, 177, 192, 0.05);
  border-radius: 8px 8px 0 0;
  overflow: hidden;
}

.table-row {
  display: flex;
  border-bottom: 1px solid var(--border-color);
  transition: background-color 0.2s;
  min-height: 60px; /* 设置行的最小高度 */
}

.table-row:hover {
  background-color: rgba(35, 177, 192, 0.05);
}

.highlight-row {
  background-color: rgba(245, 158, 11, 0.12) !important;
  animation: highlight 2s ease-out;
}

@keyframes highlight {
  0% { background-color: rgba(245, 158, 11, 0.2); }
  100% { background-color: rgba(245, 158, 11, 0.12); }
}

.table-col {
  flex: 1;
  padding: 12px;
  text-align: center; /* 宽度居中 */
  word-break: break-all;
  display: flex; /* 使用flex布局实现高度居中 */
  align-items: center; /* 垂直居中 */
  justify-content: center; /* 水平居中 */
}

/* 批次号列特殊样式 */
.table-col.batch-col {
  min-height: 70px; /* 增加批次号列的高度 */
  flex: 1.5; /* 可以根据需要调整宽度比例 */
}

/* 表头样式调整 */
.table-header .table-col {
  font-weight: 600;
  color: var(--text-secondary);
  border-right: 1px solid var(--border-color);
  min-height: 40px; /* 表头行高 */
}

.table-header .table-col:last-child {
  border-right: none;
}

/* 数量控制样式 */
.quantity-control {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 120px;
  margin: 0 auto;
}

.quantity-btn {
  width: 30px;
  height: 30px;
  border-radius: 4px;
  border: 1px solid var(--border-color);
  background-color: var(--card-background);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.2s;
  color: var(--text-primary);
}

.quantity-btn:disabled {
  background-color: rgba(35, 177, 192, 0.05);
  color: var(--text-secondary);
  cursor: not-allowed;
}

.quantity-btn:not(:disabled):hover {
  background-color: rgba(35, 177, 192, 0.12);
}

.plus-btn {
  color: #10b981;
  border-color: rgba(16, 185, 129, 0.3);
}

.minus-btn {
  color: #ef4444;
  border-color: rgba(239, 68, 68, 0.3);
}

.quantity-input {
  width: 40px;
  height: 30px;
  text-align: center;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  padding: 0;
}

.quantity-input:focus {
  border-color: var(--primary-color);
  outline: none;
}

/* 状态标签样式 */
.status-tag {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.status-0 { /* 未盘点 */
  background-color: rgba(239, 68, 68, 0.12);
  color: #ef4444;
}

.status-1 { /* 已盘点 */
  background-color: rgba(16, 185, 129, 0.12);
  color: #10b981;
}

/* 加载中状态 */
.loading-state {
  padding: 48px 0;
  text-align: center;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(35, 177, 192, 0.2);
  border-top: 4px solid var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

.loading-text {
  color: var(--text-secondary);
  font-size: 14px;
}

/* 错误提示文本 */
.error-message {
  color: #ef4444;
  font-size: 14px;
  margin: 0 0 8px 0;
  text-align: center;
  padding: 12px;
}

/* 空状态样式 */
.empty-state {
  padding: 48px 0;
  text-align: center;
  color: var(--text-secondary);
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.4;
}

.empty-text {
  font-size: 14px;
  line-height: 1.5;
}

/* 加载动画 */
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* 弹窗样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
}

.modal-container {
  background-color: var(--card-background);
  border-radius: 8px;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.modal-header {
  padding: 16px;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.modal-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
}

.modal-close {
  background: transparent;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: var(--text-secondary);
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.modal-close:hover {
  background-color: rgba(35, 177, 192, 0.12);
  color: var(--text-primary);
}

.modal-body {
  padding: 16px;
  max-height: 70vh;
  overflow-y: auto;
}

.modal-footer {
  padding: 12px 16px;
  border-top: 1px solid var(--border-color);
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.modal-cancel {
  background-color: rgba(35, 177, 192, 0.05);
  color: var(--text-secondary);
}

.modal-cancel:hover {
  background-color: rgba(35, 177, 192, 0.12);
}

.modal-confirm {
  background-color: var(--primary-color);
  color: #fff;
}

.modal-confirm:hover:not(:disabled) {
  background-color: #16a3b8;
}

.modal-confirm:disabled {
  background-color: rgba(35, 177, 192, 0.5);
  cursor: not-allowed;
}

/* 响应式适配 */
@media (max-width: 576px) {
  .header {
    padding: 3px 8px;
  }
  
  .table-col {
    padding: 8px 4px;
    font-size: 13px;
  }
  
  .source-actions {
    flex-direction: column;
  }
}
</style>
